/**
 * Customização do OTPWidget do volto-form-block.
 * Substitui o botão Semantic UI (ilegível com o tema govbr.theme)
 * por um botão `br-button secondary` do GovBR Design System.
 */

import 'volto-form-block/components/Widget/OTPWidget.css';

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { sendOTP } from 'volto-form-block/actions';
import Field from 'volto-form-block/components/Field';
import { isValidEmail } from 'volto-form-block/helpers/validators';
export const OTP_FIELDNAME_EXTENDER = '_otp';

const messages = defineMessages({
  send_otp_to: {
    id: 'form_send_otp_to',
    defaultMessage: 'Send OTP code to {email}',
  },
  insert_otp: {
    id: 'form_insert_otp',
    defaultMessage: 'Insert here the OTP code received at {email}',
  },
  otp_sent: {
    id: 'form_otp_send',
    defaultMessage:
      'OTP code was sent to {email}. Check your email and insert the received OTP code into the field above.',
  },
  otp_countdown: {
    id: 'form_otp_countdown',
    defaultMessage: 'You can send a new OTP code in',
  },
});

const getCountDownValues = (countDown) => {
  const days = Math.floor(countDown / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (countDown % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
  );
  const minutes = Math.floor((countDown % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((countDown % (1000 * 60)) / 1000);

  return [days, hours, minutes, seconds];
};

const OTPWidget = (props) => {
  const OTP_EXIPIRE_MINUTES = 5;
  const {
    id,
    title,
    fieldValue,
    onChange,
    value,
    valid,
    disabled,
    isOnEdit,
    formHasErrors,
    errorMessage,
    path,
    block_id,
  } = props;
  const intl = useIntl();
  const dispatch = useDispatch();
  const _id = id + OTP_FIELDNAME_EXTENDER;
  const sendOTPResponse = useSelector(
    (state) =>
      state.sendOTP?.subrequests?.['otp_' + block_id + '_' + fieldValue],
  );
  const [countDownEnd, setCountDownEnd] = useState(null);
  const [countDown, setCountDown] = useState(null);

  const displayWidget = isValidEmail(fieldValue);

  const sendOTPCode = () => {
    dispatch(sendOTP(path, block_id, fieldValue));
  };

  useEffect(() => {
    if (sendOTPResponse?.loaded) {
      const end = new Date().getTime() + OTP_EXIPIRE_MINUTES * 60000;
      setCountDownEnd(end);
      setCountDown(end - new Date().getTime());

      const interval = setInterval(() => {
        setCountDown(countDownEnd - new Date().getTime());
      }, 1000);

      return () => clearInterval(interval);
    } else {
      setCountDown(null);
      setCountDownEnd(null);
    }
  }, [sendOTPResponse, countDownEnd]);

  return displayWidget ? (
    <div className="otp-widget">
      <div className="otp-widget-field-wrapper">
        <div className="button-wrapper">
          {/* CUSTOMIZAÇÃO: Botão br-button do GovBR Design System para substituir o Semantic UI Button */}
          <button
            className="br-button secondary small send-otp-code"
            type="button"
            disabled={countDown > 0}
            onClick={(e) => {
              e.preventDefault();
              sendOTPCode();
            }}
          >
            {/* Fim da customização */}
            {intl.formatMessage(messages.send_otp_to, { email: fieldValue })}
          </button>
          {countDown > 0 && (
            <div className="otp-button-message">
              {intl.formatMessage(messages.otp_countdown)}{' '}
              {getCountDownValues(countDown)
                .filter((v, index) => v > 0 || index === 2 || index === 3)
                .map((v) => (v < 10 ? '0' + v : v))
                .join(':')}
              .
            </div>
          )}
        </div>

        <Field
          label={
            title ??
            intl.formatMessage(messages.insert_otp, { email: fieldValue })
          }
          name={_id}
          id={id}
          field_type="text"
          required={true}
          value={value || ''}
          onChange={onChange}
          valid={valid}
          disabled={disabled}
          formHasErrors={formHasErrors}
          errorMessage={errorMessage}
          isOnEdit={isOnEdit}
        />
      </div>

      {sendOTPResponse?.loaded && !sendOTPResponse?.loading && (
        <div className="otp-alert otp-success">
          {intl.formatMessage(messages.otp_sent, { email: fieldValue })}
        </div>
      )}

      {sendOTPResponse?.error && (
        <div className="otp-alert otp-error">
          {JSON.stringify(sendOTPResponse.error)}
        </div>
      )}
    </div>
  ) : (
    <></>
  );
};

OTPWidget.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  onClick: PropTypes.func,
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
};

export default OTPWidget;
